import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, Box, IconButton, Stack, Typography } from '@mui/material';
import useLocalization from '../../../hooks/useLocalization';
import { useZeroXDepositDestination } from '../../../hooks/payment/useZeroXDepositDestination';
import type { ZeroXAddress } from '../../../schemas/zeroXDeposit';
import { zeroXDepositStyles as styles } from '../../../styles/components/zeroXDeposit';
import ZeroXDepositSkeleton from './ZeroXDepositSkeleton';

type CopyableValueProps = {
  label: string;
  value: string;
  copied: boolean;
  copyLabel: string;
  onCopy: () => Promise<void>;
};

const CopyableValue = ({ label, value, copied, copyLabel, onCopy }: CopyableValueProps) => (
  <Stack sx={styles.field}>
    <Typography sx={styles.label}>{label}</Typography>

    <Box sx={styles.valueField}>
      <Typography sx={styles.value}>{value}</Typography>

      <IconButton
        onClick={() => void onCopy()}
        size='small'
        sx={copied ? styles.copiedButton : styles.copyButton}
        aria-label={copyLabel}
      >
        {copied ? <CheckIcon fontSize='small' /> : <ContentCopyIcon fontSize='small' />}
      </IconButton>
    </Box>
  </Stack>
);

type ZeroXDepositDestinationProps = {
  destination: Extract<ZeroXAddress, { state: 'ready' }>;
};

const ZeroXDepositDestination = ({ destination }: ZeroXDepositDestinationProps) => {
  const { t } = useLocalization();
  const { currency, address, tag } = destination;
  const { qr, qrLoading, copyFeedback, copy } = useZeroXDepositDestination(address, tag);
  const copied = copyFeedback === 'address' || copyFeedback === 'tag';

  if (qrLoading) {
    return <ZeroXDepositSkeleton currency={currency} />;
  }

  return (
    <Stack sx={styles.readyCard}>
      <Typography sx={styles.assetBadge}>{currency}</Typography>

      {tag && (
        <>
          <Alert severity='warning'>{t('zeroXTagRequired')}</Alert>

          <CopyableValue
            label={t('zeroXDestinationTag')}
            value={tag}
            copied={copyFeedback === 'tag'}
            copyLabel={t('zeroXCopyTag')}
            onCopy={() => copy(tag, 'tag')}
          />
        </>
      )}

      {qr && !tag && (
        <Box sx={styles.qrContainer}>
          <Box component='img' src={qr} alt={t('cryptoQrCodeAlt')} sx={styles.qr} />
        </Box>
      )}

      <CopyableValue
        label={t('cryptoAddress')}
        value={address}
        copied={copyFeedback === 'address'}
        copyLabel={t('copyWalletAddress')}
        onCopy={() => copy(address, 'address')}
      />

      {copyFeedback === 'error' && <Alert severity='error'>{t('zeroXCopyError')}</Alert>}

      {copied && (
        <Typography role='status' aria-live='polite' sx={styles.visuallyHidden}>
          {t(copyFeedback === 'tag' ? 'zeroXTagCopied' : 'walletAddressCopied')}
        </Typography>
      )}
    </Stack>
  );
};

export default ZeroXDepositDestination;
